import {Component, inject, input, OnInit, output, viewChild} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {RichTextEditorComponent} from '../../../../../../../shared/components/rich-text-editor/rich-text-editor.component';
import {
  ProjectStepRichTextService,
  RichTextSection,
  RichTextSectionConfig,
} from '../../../../../../../shared/services/project-step-rich-text.service';
import {Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {ProjectBaseStepComponent} from '../../../../project-base-step/project-base-step.component';
import {TabStatus} from '../../../../../../../shared/tabs/tab-status.model';
import {
  TenderInvitationPreviewControllerService,
  TenderInvitationPreviewMailUpsertRequest,
  TenderInvitationPreviewResponse,
  TenderPhaseType,
} from '@frontend/shared-api';

@Component({
  selector: 'app-step-8-2-1-rfq-mail-preview',
  providers: [ProjectStepRichTextService],
  imports: [TranslocoPipe, RichTextEditorComponent],
  templateUrl: './step-8-2-1-rfq-mail-preview.component.html',
})
export class Step821RfqMailPreviewComponent extends ProjectBaseStepComponent implements OnInit {
  private readonly invitationPreviewControllerService = inject(
    TenderInvitationPreviewControllerService,
  );
  readonly richTextService = inject(
    ProjectStepRichTextService<
      TenderInvitationPreviewMailUpsertRequest,
      TenderInvitationPreviewResponse
    >,
  );

  readonly preloadedData = input<TenderInvitationPreviewResponse | undefined>(undefined);
  readonly stepLocked = input<boolean>(false);
  readonly statusChange = output<TabStatus>();

  readonly richTextEditor = viewChild(RichTextEditorComponent);

  onStatusChange = () => {
    this.emitStatusChange();
  };

  get mailSections() {
    return this.richTextService.getSections();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeRichTextService();
  }

  private initializeRichTextService(): void {
    const config: RichTextSectionConfig<
      TenderInvitationPreviewMailUpsertRequest,
      TenderInvitationPreviewResponse
    > = {
      sections: RFQ_MAIL_PREVIEW_SECTIONS,
      fieldMap: RFQ_MAIL_PREVIEW_FIELD_MAP,
      saveFunction: (projectId, payload) => this.save(projectId, payload),
      loadFunction: (projectId) =>
        this.invitationPreviewControllerService.getTenderInvitationPreview({
          projectId,
          phase: TenderPhaseType.RFQ,
        }),
    };

    this.richTextService.initialize(this.projectId, config, this.preloadedData());
  }

  private save(
    projectId: number,
    richTextPayload: TenderInvitationPreviewMailUpsertRequest,
  ): Observable<TenderInvitationPreviewResponse> {
    return this.invitationPreviewControllerService
      .getTenderInvitationPreview({
        projectId,
        phase: TenderPhaseType.RFQ,
      })
      .pipe(
        switchMap((existingData) => {
          const completePayload: TenderInvitationPreviewMailUpsertRequest = {
            ...existingData,
            ...richTextPayload,
            emailSubject: this.richTextEditor()?.currentLabel() || existingData.emailSubject,
          };
          return this.invitationPreviewControllerService.upsertMail({
            projectId,
            phase: TenderPhaseType.RFQ,
            tenderInvitationPreviewMailUpsertRequest: completePayload,
          });
        }),
      );
  }

  getEmailSubjectForSection(): string {
    return this.preloadedData()?.emailSubject || '';
  }

  protected emitStatusChange(): void {
    const totalSections = this.richTextService.getTotalSectionsCount();
    const completedSections = this.richTextService.getCompletedSectionsCount();

    this.statusChange.emit({totalSections, completedSections});
  }
}

export const RFQ_MAIL_PREVIEW_SECTIONS: RichTextSection[] = [
  {
    key: 'step_rfq_invitation_preview_mail_preview',
    content: '',
    tooltipKey: '',
    hasChanges: false,
  },
];

export const RFQ_MAIL_PREVIEW_FIELD_MAP: Record<
  string,
  keyof TenderInvitationPreviewMailUpsertRequest
> = {
  step_rfq_invitation_preview_mail_preview: 'mailText',
};
